import "./SiteDrawer.css";

import { ControlSection } from "../../analyticsDashboard/components/ControlSection/ControlSection";
import { FilterByStatus } from "../FilterByStatus/FilterByStatus";
import { InlineSpinner } from "../../components/InlineSpinner";
import { MAP_MODES } from "../../constants";
import PropTypes from "prop-types";
import { useKeyboardShortcut } from "../../hooks/useKeyboardShortcut";
import { useMapBulkExport } from "../useMapBulkExport";
import { useSelector } from "react-redux";
import { useState } from "react";

// Site selection in a left drawer, same pattern as the PCA parameters drawer:
// a handle under the navbar, a panel over the map, a backdrop, a close tab.
// It used to be a panel pinned in the map HUD, where a cascade of three selects
// and two full-width buttons competed with the map for the top-left corner.
export const SiteDrawer = ({ defaultOpen }) => {
  const [trial, mapMode, filterByStatusMode] = useSelector(({ resultMap }) => [
    resultMap.trial,
    resultMap.mapMode,
    resultMap.filterByStatusMode,
  ]);
  const trialsLoading = useSelector(({ user }) => user.trialsLoading);
  // The URL alone is not the truth: coming back to the map from another page
  // drops the site id from the path while the store still holds the site.
  const [isOpen, setIsOpen] = useState(defaultOpen && !trial);

  // The body outlives the closing: it has to stay on screen for the slide, and
  // is dropped once the panel has left (onTransitionEnd below).
  const [showBody, setShowBody] = useState(isOpen);
  if (isOpen && !showBody) setShowBody(true);

  // Close as soon as a site is picked: the map is the point, not the picker.
  const [prevTrial, setPrevTrial] = useState(trial);
  if (trial !== prevTrial) {
    setPrevTrial(trial);
    if (trial) setIsOpen(false);
  }

  useKeyboardShortcut("s", () => setIsOpen((open) => !open));

  const bulkExport = useMapBulkExport();

  // Same condition as the markers it filters (WorldMap.jsx): TRIAL mode on an
  // opened site is the only case drawing no marker, and the store keeps the
  // filter ticked across that switch.
  const filtersMarkers = filterByStatusMode && mapMode !== MAP_MODES.TRIAL;

  return (
    <>
      <div className="site-drawer-handle">
        {/* Gone while open: the drawer shows the site itself, and a long name
            made the tab stick out past the 320px panel. */}
        {!isOpen && (
          <div
            className={`tab powerdash-component d-flex flex-row align-items-center clickable gap-2 ${
              trial ? "has-site" : ""
            }`}
            title={trial?.display_name}
            onClick={() => setIsOpen(true)}
          >
            <i className="fa-solid fa-map-pin" />
            <span className="tab-text">
              {trial ? trial.display_name : "Select a site"}
            </span>
            <kbd>S</kbd>
          </div>
        )}
        {/* The filter keeps hiding markers after the drawer is closed
            (TrialMarkerCluster.jsx), and the handle is the only part of the
            drawer still on screen: it carries the signal, and clicking it
            reopens the checkbox that undoes it. */}
        {filtersMarkers && !isOpen && (
          <div
            className="site-drawer-filter-badge clickable"
            title="Open site selection to show every site again"
            onClick={() => setIsOpen(true)}
          >
            <i className="fa-solid fa-filter" />
            Sites filtered: client action required
          </div>
        )}
      </div>

      {/* No backdrop, unlike the PCA drawer: the map has to stay pannable and
          zoomable with the drawer open, so nothing may sit over it. Closing is
          the chevron tab, the handle or the S key. */}
      <div
        className={`site-drawer ${isOpen ? "open" : ""}`}
        onTransitionEnd={(event) => {
          // Only the panel sliding counts: the transitions of the buttons
          // inside it bubble up here too.
          if (event.target === event.currentTarget && !isOpen)
            setShowBody(false);
        }}
      >
        <div className="site-drawer-header">
          <span className="site-drawer-title">
            <i className="fa-solid fa-map-pin" /> Site selection
          </span>
          {/* The list below comes from the persisted store while the map
              refetches it (WorldMapLayout.jsx): a hint, not a blocking loader. */}
          {trialsLoading && (
            <span title="Refreshing your campaigns">
              <InlineSpinner className="ms-2" />
            </span>
          )}
        </div>
        {/* Gone once the panel has left: closed, it is only translated aside,
            so its content stayed clickable and focusable, kept the 'U' upload
            shortcut of ControlSection live, and let the floating upload card
            (400px, drawn outside the 320px panel) land back over the map. */}
        {showBody && (
          <>
            <ControlSection
              alwaysExpanded
              onDownload={bulkExport.canExport ? bulkExport.open : undefined}
            />

            {mapMode !== MAP_MODES.TRIAL && <FilterByStatus />}
          </>
        )}
      </div>
      {/* Outside the body: closing the site drawer must not unmount it. */}
      {bulkExport.drawer}

      {isOpen && (
        <div
          className="site-drawer-close-tab p-3 d-flex align-items-center clickable"
          title="Hide site selection"
          onClick={() => setIsOpen(false)}
        >
          <i className="fa-solid fa-angles-left" />
        </div>
      )}
    </>
  );
};

SiteDrawer.propTypes = {
  // Open on arrival when the URL carries no site: the map is empty and the
  // picker is the only thing to do.
  defaultOpen: PropTypes.bool.isRequired,
};
